import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Renderer2,
  Signal,
  viewChild,
  signal,
  WritableSignal,
} from '@angular/core';
import { IconButtonComponent } from '@kinomap-dev/ng-ui-lib/buttons';
import { ContestHeaderComponent } from 'app/features/contest/components/contest-header/contest-header.component';
import { ToolbarContestComponent } from 'app/features/contest/components/toolbar-contest/toolbar-contest.component';
import { TrackInfo } from 'app/features/contest/models/contest-user-info.models';

import { DividerComponent } from './components/divider/divider.component';
import { ContestStore } from 'app/features/contest/data-access/contest-store';
import { ContestInformationService } from 'app/features/contest/data-access/contest-information.service';
import { ContestService } from 'app/features/contest/data-access/contest.service';
import { LeaderboardTheme } from 'app/features/contest/models/leaderboard-theme';
import { PulseComponent } from '@kinomap-dev/ng-ui-lib/loaders';
import { ToolbarDockComponent } from 'app/shared/ui/toolbar-dock/toolbar-dock.component';
import { ContestLeaderboardPanelComponent } from './components/leaderboard-panel/contest-leaderboard-panel.component';

type LeaderboardSide = 'left' | 'right';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'onWindowResize()',
    '(window:keydown.escape)': 'onEscapeKey()',
    '[style.--primary-color]': 'store.template().theme.primaryColor',
    '[style.--secondary-color]': 'store.template().theme.secondaryColor',
    '[style.--text-color]': 'store.template().theme.textColor',
  },
  imports: [
    ContestLeaderboardPanelComponent,
    NgTemplateOutlet,
    ContestHeaderComponent,
    ToolbarContestComponent,
    PulseComponent,
    DividerComponent,
    IconButtonComponent,
    ToolbarDockComponent,
  ],
  providers: [ContestStore, ContestInformationService, ContestService],
  selector: 'app-contest',
  styleUrl: './contest.component.scss',
  templateUrl: './contest.component.html',
})
export class ContestComponent {
  readonly mobileBreakpoint: number = 950;
  readonly store = inject(ContestStore);
  readonly contestService: ContestService = inject(ContestService);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly renderer: Renderer2 = inject(Renderer2);

  readonly divider: Signal<DividerComponent | undefined> = viewChild<DividerComponent>('divider');

  showRankingBlock: WritableSignal<boolean> = signal<boolean>(true);
  editionModeEnabled: WritableSignal<boolean> = signal<boolean>(false);
  isFullscreen: WritableSignal<boolean> = signal<boolean>(false);
  isMobileViewport: WritableSignal<boolean> = signal<boolean>(false);
  selectedLeaderboardOnMobile: WritableSignal<LeaderboardSide> = signal<LeaderboardSide>('left');

  disableLoading: boolean = false;
  isLoaderLeaving: boolean = false;

  constructor() {
    effect(() => {
      if (this.contestService.isReady()) {
        this.isLoaderLeaving = true;
      } else {
        this.disableLoading = false;
        this.isLoaderLeaving = false;
      }
    });

    effect(() => {
      const theme: LeaderboardTheme = this.store.template().theme;

      this.renderer.setStyle(document.body, 'backgroundColor', theme.primaryColor);
      this.renderer.setStyle(document.body, '--primary-color', theme.primaryColor);
      this.renderer.setStyle(document.body, '--secondary-color', theme.secondaryColor);
      this.renderer.setStyle(document.body, '--text-color', theme.textColor);
    });

    this.destroyRef.onDestroy(() => {
      this.renderer.removeStyle(document.body, 'backgroundColor');
      this.renderer.removeStyle(document.body, '--primary-color');
      this.renderer.removeStyle(document.body, '--secondary-color');
      this.renderer.removeStyle(document.body, '--text-color');
    });

    this.syncMobileViewport();
  }

  get videoInformation(): TrackInfo {
    return this.store.trackInfo() as TrackInfo;
  }

  onWindowResize(): void {
    const wasMobileViewport: boolean = this.isMobileViewport();
    this.syncMobileViewport();

    if (wasMobileViewport !== this.isMobileViewport()) {
      this.divider()?.reset(this.isMobileViewport());
    }
  }

  onEscapeKey(): void {
    if (this.isFullscreen()) this.isFullscreen.set(false);
  }

  getTitleBlockClasses(className: string, side?: string): string[] {
    const isSwitchableTitle: boolean =
      this.isMobileViewport() &&
      (className === 'leaderboard-title-left' || className === 'leaderboard-title-right');
    const isActiveTitle: boolean =
      this.isMobileViewport() && this.selectedLeaderboardOnMobile() === side;

    return [
      className,
      isSwitchableTitle ? 'mobile-switchable-title' : '',
      isActiveTitle ? 'mobile-title-active' : '',
    ].filter(Boolean);
  }

  isLeaderboardVisible(side: LeaderboardSide): boolean {
    if (!this.isMobileViewport() || !this.store.template().hasTwoTables) return true;

    return this.selectedLeaderboardOnMobile() === side;
  }

  loadingAnimationDone(): void {
    this.disableLoading = true;
  }

  onLoaderTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'opacity' || !this.isLoaderLeaving) return;

    this.loadingAnimationDone();
  }

  selectLeaderboardOnMobile(side?: LeaderboardSide): void {
    if (!this.isMobileViewport() || !side) return;

    this.selectedLeaderboardOnMobile.set(side);
  }

  toggleFullscreen(): void {
    this.isFullscreen.update((isFullscreen: boolean) => !isFullscreen);
  }

  private syncMobileViewport(): void {
    const isMobile: boolean = window.innerWidth < this.mobileBreakpoint;

    if (this.isMobileViewport() === isMobile) return;

    this.isMobileViewport.set(isMobile);
  }
}
